import React from 'react';
import GuidelineSection from '../components/GuidelineSection';
import SubSection from '../components/SubSection';
import TableOfContents from '../components/TableOfContents';
const EndOfLifeCare: React.FC = () => {
  const tocItems = [{
    id: 'introduction',
    title: 'Introduction',
    level: 1
  }, {
    id: 'hospice',
    title: 'Hospice',
    level: 1
  }, {
    id: 'medical-aid',
    title: 'Medical Aid in Dying',
    level: 1
  }];
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-192">
      <div className="lg:col-span-3" data-id="element-193">
        <GuidelineSection id="end-of-life-care" title="6. END OF LIFE CARE" data-id="element-194">
          <SubSection id="introduction" title="6.1 Introduction" data-id="element-195">
            <p className="mb-4" data-id="element-196">
              When the decision is made to stop pursuing new treatments focused
              on controlling cancer, high-quality end-of-life care can help
              ensure comfort, dignity, and quality of remaining time. This is
              not 'giving up'—it's shifting the focus to symptom management and
              quality of life.
            </p>
            <p className="mb-4" data-id="element-197">Options available include:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-198">
              <li data-id="element-199">Hospice care (home or facility-based)</li>
              <li data-id="element-200">Palliative care continuation</li>
              <li data-id="element-201">Medical Aid in Dying (where legally available)</li>
              <li data-id="element-202">Spiritual and psychosocial support</li>
            </ul>
          </SubSection>
          <SubSection id="hospice" title="6.2 Hospice" data-id="element-203">
            <p className="mb-4" data-id="element-204">
              <strong data-id="element-205">Overview:</strong> Hospice provides comprehensive comfort
              care when life expectancy is estimated at 6 months or less (if the
              disease runs its natural course). Hospice focuses on:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-206">
              <li data-id="element-207">Pain and symptom management</li>
              <li data-id="element-208">Emotional and spiritual support</li>
              <li data-id="element-209">Family/caregiver support</li>
              <li data-id="element-210">Practical assistance</li>
            </ul>
            <div className="bg-yellow-50 p-4 rounded-lg mb-6" data-id="element-211">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-212">
                Hospice does NOT mean:
              </h4>
              <ul className="list-disc pl-6 space-y-2" data-id="element-213">
                <li data-id="element-214">You are giving up</li>
                <li data-id="element-215">You will die immediately</li>
                <li data-id="element-216">
                  You can't change your mind (you can leave hospice if you
                  improve or want to pursue treatment)
                </li>
              </ul>
            </div>
            <p className="font-medium mb-2" data-id="element-217">Options:</p>
            <p className="font-medium mb-2" data-id="element-218">Home Hospice:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-219">
              <li data-id="element-220">Care provided in your home</li>
              <li data-id="element-221">
                Visits from hospice team (nurses, aides, social workers,
                chaplains)
              </li>
              <li data-id="element-222">24/7 phone support</li>
              <li data-id="element-223">Medical equipment and medications provided</li>
              <li data-id="element-224">Most common and preferred option</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-225">Inpatient Hospice:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-226">
              <li data-id="element-227">Facility-based care</li>
              <li data-id="element-228">For complex symptom management</li>
              <li data-id="element-229">Respite care for caregivers</li>
              <li data-id="element-230">Continuous care during crisis</li>
            </ul>
            <p className="font-medium mb-2" data-id="element-231">How to Access:</p>
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-232">
              <div className="flex items-center" data-id="element-233">
                <input type="checkbox" className="mr-2" data-id="element-234" />
                <span data-id="element-235">
                  Referral from your oncologist or primary care physician
                </span>
              </div>
              <div className="flex items-center" data-id="element-236">
                <input type="checkbox" className="mr-2" data-id="element-237" />
                <span data-id="element-238">
                  Medicare, Medicaid, and most insurance cover hospice
                </span>
              </div>
              <div className="flex items-center" data-id="element-239">
                <input type="checkbox" className="mr-2" data-id="element-240" />
                <span data-id="element-241">Veterans Affairs (VA) provides hospice benefits</span>
              </div>
            </div>
            <p className="font-medium mb-2" data-id="element-242">Resources:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-243">
              <li data-id="element-244">
                National Hospice and Palliative Care Organization (NHPCO):
                nhpco.org
              </li>
              <li data-id="element-245">Hospice Foundation of America: hospicefoundation.org</li>
              <li data-id="element-246">Local hospice agencies in your area</li>
            </ul>
          </SubSection>
          <SubSection id="medical-aid" title="6.3 Medical Aid in Dying (MAID)" data-id="element-247">
            <p className="mb-4" data-id="element-248">
              <strong data-id="element-249">Overview:</strong> Medical Aid in Dying (MAID) or Death
              with Dignity allows terminally ill, mentally competent adults to
              request medication that they can self-administer to end their life
              peacefully. This is legal in some U.S. states and countries.
            </p>
            <p className="font-medium mb-2" data-id="element-250">
              Where Legal in the United States (as of 2025):[74]
            </p>
            <p className="mb-4" data-id="element-251">11 U.S. Jurisdictions:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6" data-id="element-252">
              <div data-id="element-253">- Oregon</div>
              <div data-id="element-254">- Washington</div>
              <div data-id="element-255">- California</div>
              <div data-id="element-256">- Colorado</div>
              <div data-id="element-257">- Vermont (no residency requirement)[75]</div>
              <div data-id="element-258">- Montana</div>
              <div data-id="element-259">- New Jersey</div>
              <div data-id="element-260">- Hawaii</div>
              <div data-id="element-261">- Maine</div>
              <div data-id="element-262">- New Mexico</div>
              <div data-id="element-263">- Delaware (expanded 2025)[76]</div>
              <div data-id="element-264">- District of Columbia</div>
            </div>
            <p className="font-medium mb-2" data-id="element-265">Recent Expansions:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-266">
              <li data-id="element-267">
                Oregon and Vermont removed residency requirements in
                2022-2023[77]
              </li>
              <li data-id="element-268">
                Delaware became the 11th state to legalize MAID in 2025[78]
              </li>
              <li data-id="element-269">
                Colorado and Delaware expanded provider roles in 2024-2025 to
                include Advanced Practice Registered Nurses as attending or
                consulting clinicians[79]
              </li>
            </ul>
            <p className="font-medium mb-2" data-id="element-270">
              International Legalization (10+ Countries):[80]
            </p>
            <p className="mb-2" data-id="element-271">Full legalization exists in:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4" data-id="element-272">
              <div data-id="element-273">- Switzerland (1942 - oldest law)</div>
              <div data-id="element-274">- Netherlands (2001)</div>
              <div data-id="element-275">- Belgium (2002)</div>
              <div data-id="element-276">- Luxembourg(2009)</div>
              <div data-id="element-277">- Canada (2016)</div>
              <div data-id="element-278">- Spain (2021)</div>
              <div data-id="element-279">- Austria (via 2020 court ruling)</div>
              <div data-id="element-280">
                - Germany (via 2020 constitutional court ruling, framework
                incomplete)
              </div>
              <div data-id="element-281">- Australia (legal in all 6 states by 2022)</div>
              <div data-id="element-282">- New Zealand (2021)</div>
              <div data-id="element-283">- Portugal (2023)</div>
            </div>
            <p className="font-medium mb-2" data-id="element-284">Advancing Legislation:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-285">
              <li data-id="element-286">
                England and Wales: House of Commons passed the Terminally Ill
                Adults (End of Life) Bill in June 2025 (314-291 vote), now
                proceeding to House of Lords with expected passage into law[81]
              </li>
            </ul>
            <p className="font-medium mb-2" data-id="element-287">
              General U.S. Eligibility Requirements (vary by jurisdiction):[82]
            </p>
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-288">
              <div className="flex items-center" data-id="element-289">
                <input type="checkbox" className="mr-2" data-id="element-290" />
                <span data-id="element-291">Age 18 years or older (adult)</span>
              </div>
              <div className="flex items-center" data-id="element-292">
                <input type="checkbox" className="mr-2" data-id="element-293" />
                <span data-id="element-294">State residency (except Oregon and Vermont)</span>
              </div>
              <div className="flex items-center" data-id="element-295">
                <input type="checkbox" className="mr-2" data-id="element-296" />
                <span data-id="element-297">
                  Terminal illness diagnosis with prognosis of 6 months or less
                </span>
              </div>
              <div className="flex items-center" data-id="element-298">
                <input type="checkbox" className="mr-2" data-id="element-299" />
                <span data-id="element-300">Mentally competent to make healthcare decisions</span>
              </div>
              <div className="flex items-center" data-id="element-301">
                <input type="checkbox" className="mr-2" data-id="element-302" />
                <span data-id="element-303">Able to self-administer medication</span>
              </div>
              <div className="flex items-center" data-id="element-304">
                <input type="checkbox" className="mr-2" data-id="element-305" />
                <span data-id="element-306">Voluntary request without coercion</span>
              </div>
              <div className="flex items-center" data-id="element-307">
                <input type="checkbox" className="mr-2" data-id="element-308" />
                <span data-id="element-309">
                  Physical capability for self-administration (oral, rectal, or
                  feeding tube)
                </span>
              </div>
            </div>
            <p className="font-medium mb-2" data-id="element-310">Safeguards Include:[83]</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-311">
              <li data-id="element-312">
                Two physician confirmations of terminal diagnosis and mental
                capacity
              </li>
              <li data-id="element-313">
                Multiple oral requests (typically two with waiting period) plus
                one written request witnessed by two adults not beneficiaries
              </li>
              <li data-id="element-314">Mandatory waiting periods between requests</li>
              <li data-id="element-315">
                Mental health evaluation if either physician suspects impaired
                judgment
              </li>
              <li data-id="element-316">Right to rescind at any time</li>
            </ul>
            <div className="bg-yellow-50 p-4 rounded-lg mb-6" data-id="element-317">
              <h4 className="font-semibold text-yellow-800 mb-2" data-id="element-318">
                Important Note on Canada:
              </h4>
              <p data-id="element-319">
                Canada employs a different model requiring "grievous and
                irremediable medical condition" causing unbearable suffering
                without requirement for reasonably foreseeable death (removed
                2021), representing more expansive criteria than U.S.
                standards.[84]
              </p>
            </div>
            <p className="font-medium mb-2" data-id="element-320">Resources:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2" data-id="element-321">
              <li data-id="element-322">Compassion & Choices: compassionandchoices.org</li>
              <li data-id="element-323">Death with Dignity National Center: deathwithdignity.org</li>
              <li data-id="element-324">State-specific organizations</li>
            </ul>
            <p className="mt-6 italic" data-id="element-325">
              Note: This is a deeply personal decision. Discuss with your
              healthcare team, family, and spiritual advisors. Not all
              physicians participate in MAID programs.
            </p>
          </SubSection>
        </GuidelineSection>
      </div>
      <div className="lg:col-span-1" data-id="element-326">
        <div className="lg:sticky lg:top-20" data-id="element-327">
          <TableOfContents items={tocItems} data-id="element-328" />
        </div>
      </div>
    </div>;
};
export default EndOfLifeCare;